// Synced YouTube viewer over the board. The teacher's player is the source of
// truth (its state is polled and relayed once per second); student players
// follow: play/pause mirrored, drift over ~2.5s snapped back. Students start
// muted (browser autoplay policy) with a one-tap sound-on button.
// KEEP IN SYNC with the dashboard original.
import { useEffect, useRef, useState } from "react";
import { WS_TYPES } from "../wsProtocol";

let apiPromise = null;
function loadYouTubeApi() {
    if (apiPromise) return apiPromise;
    apiPromise = new Promise((resolve) => {
        if (window.YT && window.YT.Player) return resolve(window.YT);
        const previous = window.onYouTubeIframeAPIReady;
        window.onYouTubeIframeAPIReady = () => {
            if (previous) previous();
            resolve(window.YT);
        };
        const script = document.createElement("script");
        script.src = "https://www.youtube.com/iframe_api";
        document.head.appendChild(script);
    });
    return apiPromise;
}

const YouTubeOverlay = ({ video, timeOffset, isTeacher, send, closeLabel, soundLabel }) => {
    const hostRef = useRef(null);
    const playerRef = useRef(null);
    const readyRef = useRef(false);
    const [needsSound, setNeedsSound] = useState(!isTeacher);
    const videoId = video && video.videoId;

    // (re)create the player when the video changes
    useEffect(() => {
        if (!videoId) return;
        let cancelled = false;
        readyRef.current = false;
        setNeedsSound(!isTeacher);

        loadYouTubeApi().then((YT) => {
            if (cancelled || !hostRef.current) return;
            playerRef.current = new YT.Player(hostRef.current, {
                width: "100%",
                height: "100%",
                videoId,
                playerVars: isTeacher
                    ? { rel: 0, playsinline: 1 }
                    : { rel: 0, playsinline: 1, controls: 0, disablekb: 1 },
                events: {
                    onReady: (event) => {
                        readyRef.current = true;
                        if (!isTeacher) event.target.mute();
                    },
                },
            });
        });

        return () => {
            cancelled = true;
            if (playerRef.current && playerRef.current.destroy) playerRef.current.destroy();
            playerRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [videoId, isTeacher]);

    // teacher: report state once per second
    useEffect(() => {
        if (!isTeacher || !videoId) return;
        const interval = setInterval(() => {
            const player = playerRef.current;
            if (!player || !readyRef.current || !player.getCurrentTime) return;
            send({
                t: WS_TYPES.YT_STATE,
                playing: player.getPlayerState && player.getPlayerState() === 1,
                time: Math.round(player.getCurrentTime() * 10) / 10,
            });
        }, 1000);
        return () => clearInterval(interval);
    }, [isTeacher, videoId, send]);

    // student: follow the relayed state
    useEffect(() => {
        if (isTeacher || !video || !readyRef.current) return;
        const player = playerRef.current;
        if (!player || !player.getPlayerState) return;

        const elapsed = video.playing ? (Date.now() + (timeOffset || 0) - video.at) / 1000 : 0;
        const expected = video.time + elapsed;
        const localPlaying = player.getPlayerState() === 1;
        const drift = Math.abs((player.getCurrentTime ? player.getCurrentTime() : 0) - expected);

        if (video.playing && !localPlaying) {
            player.seekTo(expected, true);
            player.playVideo();
        } else if (!video.playing && localPlaying) {
            player.pauseVideo();
            player.seekTo(expected, true);
        } else if (video.playing && drift > 2.5) {
            player.seekTo(expected, true);
        }
    }, [isTeacher, video, timeOffset]);

    if (!videoId) return null;

    return (
        <div
            style={{
                position: "absolute",
                inset: 0,
                zIndex: 6,
                background: "rgba(10,16,22,0.92)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}
        >
            <div style={{ position: "relative", width: "min(92%, 960px)", aspectRatio: "16/9" }}>
                <div ref={hostRef} style={{ width: "100%", height: "100%" }} />
                {/* students can't operate the player directly — the teacher drives */}
                {!isTeacher && <div style={{ position: "absolute", inset: 0 }} />}
                {!isTeacher && needsSound && (
                    <button
                        type="button"
                        onClick={() => {
                            const player = playerRef.current;
                            if (player && player.unMute) {
                                player.unMute();
                                player.setVolume(100);
                            }
                            setNeedsSound(false);
                        }}
                        style={{
                            position: "absolute",
                            bottom: 16,
                            left: "50%",
                            transform: "translateX(-50%)",
                            border: "none",
                            borderRadius: 999,
                            padding: "10px 24px",
                            fontWeight: 800,
                            fontSize: "1rem",
                            cursor: "pointer",
                            background: "#f6bf26",
                            color: "#17202a",
                            boxShadow: "0 4px 20px rgba(0,0,0,0.5)",
                        }}
                    >
                        🔊 {soundLabel}
                    </button>
                )}
                {isTeacher && (
                    <button
                        type="button"
                        onClick={() => send({ t: WS_TYPES.YT_CLOSE })}
                        style={{
                            position: "absolute",
                            top: -14,
                            insetInlineEnd: -14,
                            border: "none",
                            borderRadius: "50%",
                            width: 38,
                            height: 38,
                            fontWeight: 800,
                            cursor: "pointer",
                            background: "#e53935",
                            color: "#fff",
                            boxShadow: "0 4px 16px rgba(0,0,0,0.5)",
                        }}
                        title={closeLabel}
                    >
                        ✕
                    </button>
                )}
            </div>
        </div>
    );
};

export default YouTubeOverlay;
